import { useMemo, useState } from 'react';
import type { LeadsPais, PeriodoLeads } from '@/types/leadsDashboard';
import { n, pct, usd, usd2 } from '../format';

/**
 * Bloco 5.5 — Leads por país.
 *
 * Tabela ordenável por qualquer coluna. Clicar numa linha aplica o filtro de
 * país na tela inteira e limpa o de página (§3).
 *
 * "Alcançados no período" e "LTV" ficam lado a lado de propósito: o LTV é
 * alimentado pela coluna de alcançados, e ver as duas juntas torna a conta
 * auditável sem sair da tela.
 */

type ChaveOrdem = keyof Pick<LeadsPais,
    'captados' | 'perdidos_551' | 'saldo' | 'crescimento_pct' | 'base_total' |
    'alcancaveis' | 'alcancados_periodo' | 'taxa_alcance_pct' | 'ctr_pct' |
    'receita' | 'ltv_por_lead_alcancado'>;

const COLUNAS: { k: ChaveOrdem; l: string; cls?: string }[] = [
    { k: 'captados', l: 'Captados' },
    { k: 'perdidos_551', l: 'Perdidos 551' },
    { k: 'saldo', l: 'Saldo' },
    { k: 'crescimento_pct', l: 'Crescimento' },
    { k: 'base_total', l: 'Base total' },
    { k: 'alcancaveis', l: 'Ainda alcançáveis' },
    { k: 'alcancados_periodo', l: 'Alcançados no período', cls: 'alc' },
    { k: 'taxa_alcance_pct', l: '% alcance' },
    { k: 'ctr_pct', l: 'CTR' },
    { k: 'receita', l: 'Receita' },
    { k: 'ltv_por_lead_alcancado', l: 'LTV / lead alcançado', cls: 'ltv' },
];

const PERIODOS: { k: PeriodoLeads; l: string }[] = [
    { k: 'hoje', l: 'Hoje' }, { k: 'ontem', l: 'Ontem' },
    { k: '7d', l: '7 dias' }, { k: '30d', l: '30 dias' }, { k: 'mes', l: 'Este mês' },
];

interface Props {
    itens: LeadsPais[];
    paisSelecionado: string | null;
    periodo: PeriodoLeads;
    rotulo: string;
    moeda: string;
    onPais: (codigo: string | null) => void;
    onPeriodo: (p: PeriodoLeads) => void;
}

export default function TabelaPaises({
    itens, paisSelecionado, periodo, rotulo, moeda, onPais, onPeriodo,
}: Props) {
    const [ordem, setOrdem] = useState<{ c: ChaveOrdem; d: 'asc' | 'desc' }>({
        c: 'captados', d: 'desc',
    });

    const ordenados = useMemo(() => {
        const copia = [...itens];
        copia.sort((a, b) =>
            ordem.d === 'desc' ? b[ordem.c] - a[ordem.c] : a[ordem.c] - b[ordem.c]
        );
        return copia;
    }, [itens, ordem]);

    const maxCaptados = Math.max(...itens.map(p => p.captados), 1);

    const alternar = (c: ChaveOrdem) =>
        setOrdem(o => ({ c, d: o.c === c && o.d === 'desc' ? 'asc' : 'desc' }));

    return (
        <div className="card">
            <div className="card-h">
                <div>
                    <div className="card-t">Leads por país</div>
                    <div className="card-s">
                        Clique numa linha para filtrar a tela inteira. Período: {rotulo}.
                    </div>
                </div>
                {/* Filtro só deste bloco. O período do topo devolve a tabela a ele. */}
                <div className="seg">
                    {PERIODOS.map(p => (
                        <button
                            key={p.k} type="button"
                            data-on={periodo === p.k ? 1 : 0}
                            onClick={() => onPeriodo(p.k)}
                        >
                            {p.l}
                        </button>
                    ))}
                </div>
            </div>

            {itens.length === 0 ? (
                <div className="empty">Nenhum país com movimento neste período.</div>
            ) : (
                <div className="tblwrap">
                    <table>
                        <thead>
                            <tr>
                                <th onClick={() => alternar('base_total')} data-s={ordem.c === 'base_total' ? 1 : 0}>
                                    País
                                </th>
                                {COLUNAS.map(c => (
                                    <th
                                        key={c.k} className={c.cls}
                                        onClick={() => alternar(c.k)}
                                        data-s={ordem.c === c.k ? 1 : 0}
                                    >
                                        {c.l}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {ordenados.map(p => (
                                <tr
                                    key={p.codigo}
                                    data-sel={paisSelecionado === p.codigo ? 1 : 0}
                                    onClick={() => onPais(paisSelecionado === p.codigo ? null : p.codigo)}
                                >
                                    <td style={{ minWidth: 190 }}>
                                        <div>
                                            <span className="code">{p.codigo}</span>
                                            <span style={{ fontWeight: 550 }}>{p.nome}</span>
                                            <span className="muted" style={{ fontSize: 11, marginLeft: 7 }}>
                                                {p.qtd_paginas} pág.
                                            </span>
                                        </div>
                                        <div className="minibar">
                                            <i style={{
                                                width: `${(p.captados / maxCaptados) * 100}%`,
                                                background: 'var(--blue)',
                                            }} />
                                        </div>
                                    </td>
                                    <td className="num" style={{ fontWeight: 620 }}>{n(p.captados)}</td>
                                    <td className="num" style={{ color: 'var(--red)' }}>−{n(p.perdidos_551)}</td>
                                    <td className="num" style={{
                                        fontWeight: 600,
                                        color: p.saldo >= 0 ? 'var(--green)' : 'var(--red)',
                                    }}>
                                        {p.saldo >= 0 ? '+' : '−'}{n(Math.abs(p.saldo))}
                                    </td>
                                    <td className={`num ${p.crescimento_pct >= 0 ? 'lx-up' : 'lx-down'}`}>
                                        {p.crescimento_pct >= 0 ? '▲' : '▼'} {pct(Math.abs(p.crescimento_pct), 0)}
                                    </td>
                                    <td className="num muted">{n(p.base_total)}</td>
                                    <td className="num muted">{n(p.alcancaveis)}</td>
                                    <td className="num alc">
                                        <div style={{ fontWeight: 620, color: 'var(--violet)' }}>
                                            {n(p.alcancados_periodo)}
                                        </div>
                                        {/* O denominador vai NOMEADO aqui. A coluna ao lado mostra o
                                            estoque de agora (51.425 na França), e a cobertura usa o
                                            universo do período (109.669) — ver os dois sem dizer qual
                                            é qual fazia 104.186 sobre 51.425 dar "95%". */}
                                        <div className="sub2">
                                            {pct(p.cobertura_pct, 0)} dos {n(p.alcancaveis_no_periodo)} alcançáveis no período
                                        </div>
                                    </td>
                                    <td className="num" style={{
                                        color: p.taxa_alcance_pct < 45 ? 'var(--amber)' : 'inherit',
                                    }}>
                                        {pct(p.taxa_alcance_pct, 0)}
                                    </td>
                                    <td className="num" style={{ color: 'var(--teal)' }}>{pct(p.ctr_pct, 2)}</td>
                                    <td className="num">{usd(p.receita, moeda)}</td>
                                    <td className="num ltv">{usd2(p.ltv_por_lead_alcancado, moeda)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
